import { useCallback, useEffect, useRef, useState } from "react";

const SUPPORTED_EXTENSIONS = new Set([
  "jpg",
  "jpeg",
  "jpe",
  "jfif",
  "png",
  "webp",
  "heic",
  "heif",
  "mp4",
  "mov",
  "webm",
  "m4v",
]);

const getFileExtension = (file) => {
  const name = typeof file?.name === "string" ? file.name : "";
  if (!name.includes(".")) return "";
  return name.split(".").pop().trim().toLowerCase();
};

const getMediaType = (file) => {
  const mimeType = typeof file?.type === "string" ? file.type.toLowerCase() : "";
  const extension = getFileExtension(file);

  if (
    mimeType.startsWith("video/") ||
    ["mp4", "mov", "webm", "m4v"].includes(extension)
  ) {
    return "video";
  }

  if (
    mimeType.startsWith("image/") ||
    ["jpg", "jpeg", "jpe", "jfif", "png", "webp", "heic", "heif"].includes(
      extension
    )
  ) {
    return "image";
  }

  return "";
};

const isSupportedMediaFile = (file) => {
  if (!file) return false;

  const mimeType = typeof file.type === "string" ? file.type.toLowerCase() : "";
  return (
    mimeType.startsWith("image/") ||
    mimeType.startsWith("video/") ||
    SUPPORTED_EXTENSIONS.has(getFileExtension(file))
  );
};

export default function useEditPostMediaReplacement(post) {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [mediaType, setMediaType] = useState("");
  const [errorKey, setErrorKey] = useState("");
  const [previewUnavailable, setPreviewUnavailable] = useState(false);
  const fileInputRef = useRef(null);
  const previewUrlRef = useRef("");

  const revokePreviewUrl = useCallback(() => {
    if (
      previewUrlRef.current &&
      typeof URL !== "undefined" &&
      typeof URL.revokeObjectURL === "function"
    ) {
      URL.revokeObjectURL(previewUrlRef.current);
    }
    previewUrlRef.current = "";
  }, []);

  const clearSelectedMedia = useCallback(() => {
    revokePreviewUrl();
    setSelectedFile(null);
    setPreviewUrl("");
    setMediaType("");
    setErrorKey("");
    setPreviewUnavailable(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [revokePreviewUrl]);

  const handleFileChange = useCallback(
    (event) => {
      const file = event.target.files?.[0] || null;
      revokePreviewUrl();
      setSelectedFile(null);
      setPreviewUrl("");
      setMediaType("");
      setErrorKey("");
      setPreviewUnavailable(false);

      if (!file) return;

      if (!isSupportedMediaFile(file)) {
        setErrorKey("post_edit_media_invalid");
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      let nextPreviewUrl = "";
      if (
        typeof URL !== "undefined" &&
        typeof URL.createObjectURL === "function"
      ) {
        nextPreviewUrl = URL.createObjectURL(file);
        previewUrlRef.current = nextPreviewUrl;
      }

      setSelectedFile(file);
      setPreviewUrl(nextPreviewUrl);
      setMediaType(getMediaType(file));
    },
    [revokePreviewUrl]
  );

  useEffect(() => clearSelectedMedia, [clearSelectedMedia, post?.id]);

  const handlePreviewError = useCallback(() => {
    setPreviewUnavailable(true);
  }, []);

  return {
    selectedFile,
    previewUrl,
    mediaType,
    errorKey,
    previewUnavailable,
    fileInputRef,
    hasSelection: Boolean(selectedFile),
    clearSelectedMedia,
    handleFileChange,
    handlePreviewError,
  };
}
